import React, {useEffect, useState} from 'react';
import {Button, Form, Modal, Radio, Table} from 'antd';
import {OrderType} from "../graphql/types/OrderType";
import ImageUploader from './ImageUploader';
import { t } from 'i18n';

interface SetDeliverStatusModalProps {
    visible: boolean;
    onClose: () => void;
    onConfirm: (values: SetDeliverStatusData) => void;
    loading?: boolean;
    parcels: OrderType[];
}

export interface SetDeliverStatusData {
    orderList: {
        trackingNumber: string;
        podImageS3KeysJson: string[];
    }[];
}

interface FormValues {
    parcels: {
        orderId: string;
        photos: string[];
    }[];
}

const SetDeliverStatusModal: React.FC<SetDeliverStatusModalProps> = ({
    visible,
    onClose,
    onConfirm,
    loading = false,
    parcels
}) => {
    const [form] = Form.useForm<FormValues>();
    const [deliverStatus, setDeliverStatus] = useState<string>('delivered');

    useEffect(() => {
        if (visible) {
            form.resetFields();
            setDeliverStatus('delivered');
            const initialValues: FormValues = {
                parcels: parcels.map(parcel => ({
                    orderId: parcel.id,
                    photos: []
                }))
            };
            form.setFieldsValue(initialValues);
        }
    }, [visible, parcels, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const { parcels: parcelValues } = values;
            
            // 收集每一行的数据，每组包含订单号和上传的图片路径
            const orderList = parcelValues.map((parcel, index) => ({
                trackingNumber: parcels[index]?.trackingNumber || parcels[index]?.id || '',
                podImageS3KeysJson: parcel.photos || []
            }));
            
            onConfirm({
                orderList: orderList
            });
            onClose();
        } catch (error) {
            // 验证失败时，错误信息会直接显示在对应的Form.Item下方
            console.log('Validation failed:', error);
        }
    };

    const handleStatusChange = (e: any) => {
        setDeliverStatus(e.target.value);
    };

    return (
        <Modal
            title={
                <div className="flex items-center">
                    <div className="w-1 h-6 bg-violet-600 mr-2"></div>
                    <span>{t('components.modal.setDeliverStatus')}</span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={[
                <Button key="submit" type="primary" onClick={handleSubmit} loading={loading} className="bg-violet-600">
                    {t('components.button.set')}
                </Button>,
                <Button key="cancel" onClick={onClose} disabled={loading} className="bg-gray-100 text-gray-600">
                    {t('components.button.cancel')}
                </Button>,
            ]}
            width={950}
            maskClosable={false}
            closable={!loading}
        >
            <Form
                form={form}
                layout="horizontal"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 24 }}
                disabled={loading}
            >
                <div className="mb-4">
                    <p className="text-gray-500 mb-4">{t('components.modal.setDeliverStatusForParcels', { count: parcels.length })}</p>
                    <Radio.Group
                        value={deliverStatus}
                        onChange={handleStatusChange}
                        className="w-full"
                    >
                        <div className="flex flex-col gap-3">
                            <Radio value="delivered">{t('components.modal.delivered')}</Radio>
                        </div>
                    </Radio.Group>
                </div>
                <Form.Item name="photos">
                    <p className="text-gray-500 mb-4">{t('components.modal.podPhotos')}</p>
                    <Form.Item name="parcels" noStyle>
                        <Table
                            dataSource={parcels}
                            pagination={false}
                            size="small"
                            columns={[
                                {
                                    title: t('components.modal.trackingNumber'),
                                    dataIndex: 'trackingNumber',
                                    key: 'trackingNumber',
                                    width: 150,
                                    render: (_, record, idx) => parcels[idx]?.trackingNumber || record.id
                                },
                                {
                                    title: t('components.modal.photo'),
                                    key: 'photo',
                                    render: (_, __, idx) => (
                                        <Form.Item
                                            name={['parcels', idx, 'photos']}
                                            rules={[
                                                {
                                                    validator: (_, value) => {
                                                        if (!value || value.length < 3 || value.length > 6) {
                                                            return Promise.reject(new Error(t('components.modal.upload3To6Images')));
                                                        }
                                                        return Promise.resolve();
                                                    }
                                                }
                                            ]}
                                        >
                                            <ImageUploader
                                                maxCount={6}
                                                tag="deliver"
                                            />
                                        </Form.Item>
                                    )
                                }
                            ]}
                            rowKey={(_, idx) => (idx !== undefined ? idx.toString() : '')}
                        />
                    </Form.Item>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default SetDeliverStatusModal;
